'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useChat } from '@/lib/hooks/useChat';
import Row1ExecHero from './Row1ExecHero';
import Row2KPIStrip, { type KpiCard } from './Row2KPIStrip';
import Row3GrowthTrajectory from './Row3GrowthTrajectory';
import Row4DeptHealthMatrix, { type DeptCard } from './Row4DeptHealthMatrix';
import Row5FinancialPulse from './Row5FinancialPulse';
import Row6RiskAndAction from './Row6RiskAndAction';
import Row7OutsideTheBuilding from './Row7OutsideTheBuilding';
import Row8ExternalAndRisk from './Row8ExternalAndRisk';
import Row9TeamAndCulture from './Row9TeamAndCulture';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (val: number): string => {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
};

const BRIEFING_CACHE_KEY = 'exec_briefing_cache';

// ─── Fallback data (used until live fetch resolves) ─────────────────────────

const KPI_CARDS_FALLBACK: KpiCard[] = [
  { id: 'revenue', label: 'YTD Gross Revenue',         value: '—', sub: 'Loading...', trend: 'up',        color: '#10B981', icon: 'CurrencyDollarIcon' },
  { id: 'margin',  label: 'Company Gross Margin',      value: '—', sub: 'Loading...', trend: 'up',        color: '#07B0E9', icon: 'ChartPieIcon' },
  { id: 'cac',     label: 'Customer Acquisition Cost', value: '—', sub: 'Loading...', trend: 'down-good', color: '#033494', icon: 'UserPlusIcon' },
  { id: 'rating',  label: 'Avg. Customer Rating',      value: '—', sub: 'Loading...', trend: 'up',        color: '#F59E0B', icon: 'StarIcon' },
];

const DEPT_CARDS_FALLBACK: DeptCard[] = [
  { id: 'sales',      label: 'Sales & Estimates',  icon: 'ChartBarIcon',           status: 'green', route: '/project-manager-dashboard',                metrics: [{ label: 'Instant Estimates (30d)', value: '—' }, { label: 'Conversion Rate', value: '—' }, { label: 'Booked Revenue', value: '—' }] },
  { id: 'operations', label: 'Operations & PMs',   icon: 'CogIcon',                status: 'green', route: '/operations-command-center-dashboard',      metrics: [{ label: 'Active Roofs Today', value: '—' }, { label: 'Avg Install Time', value: '—' }, { label: 'Change Order Freq.', value: '—' }] },
  { id: 'logistics',  label: 'Logistics & Supply', icon: 'TruckIcon',              status: 'green', route: '/logistics-supply-chain-dashboard',         metrics: [{ label: 'On-Time Delivery', value: '—' }, { label: 'Active Weather Delays', value: '—' }, { label: 'Supplier Health Score', value: '—' }] },
  { id: 'finance',    label: 'Finance',            icon: 'BanknotesIcon',          status: 'green', route: '/financial-accounting-dashboard',           metrics: [{ label: 'Cash in Escrow', value: '—' }, { label: 'AR vs AP Balance', value: '—' }, { label: 'Monthly Runway', value: '—' }] },
  { id: 'quality',    label: 'Quality & Warranty', icon: 'ShieldCheckIcon',        status: 'green', route: '/warranty-claims-dashboard',                metrics: [{ label: 'First-Time Pass Rate', value: '—' }, { label: 'Active Warranty Claims', value: '—' }, { label: 'Avg Claim Resolution', value: '—' }] },
  { id: 'installers', label: 'Installer Network',  icon: 'WrenchScrewdriverIcon', status: 'green', route: '/installer-network-crew-management-dashboard', metrics: [{ label: 'Total Active Crews', value: '—' }, { label: 'Network Capacity', value: '—' }, { label: 'Compliance Drop-offs', value: '—' }] },
];

// ─── System status pill (header) ────────────────────────────────────────────

function SystemStatusPill({ status }: { status: 'green' | 'yellow' | 'red' }) {
  const cfg =
    status === 'red'    ? { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)',  dot: '#EF4444', label: 'Critical issues' }
    : status === 'yellow' ? { color: '#92400E', bg: 'rgba(245,158,11,0.10)', dot: '#F59E0B', label: 'At risk' }
    :                       { color: '#047857', bg: 'rgba(16,185,129,0.10)', dot: '#10B981', label: 'All systems normal' };
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

const ExecutiveCommandCenterInteractive = () => {
  const { currentUser } = useAuth();
  const supabase = createClient();

  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'quarter'>('month');
  const [reportGenerating, setReportGenerating] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const [kpiCards, setKpiCards] = useState<KpiCard[]>(KPI_CARDS_FALLBACK);
  const [deptCards, setDeptCards] = useState<DeptCard[]>(DEPT_CARDS_FALLBACK);
  const [loadingKpis, setLoadingKpis] = useState(true);

  const [aiBriefing, setAiBriefing] = useState<string | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const briefingFetched = useRef(false);
  const { response: chatResponse, isLoading: chatLoading, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);

  // ── Fetch live KPI + Dept data ───────────────────────────────────────────
  // Logic preserved verbatim from the previous mega-component. Real wiring;
  // hardcoded fallbacks are a separate cleanup PR (see commit message).
  const fetchKpis = useCallback(async () => {
    try {
      const [metricsRes, jobsRes, crewsRes, invoicesRes] = await Promise.all([
        supabase.from('dashboard_metrics').select('metric_key, metric_value, metric_label, trend_direction, trend_percentage, period_label').eq('category', 'executive'),
        supabase.from('roofing_jobs').select('status, contract_value'),
        supabase.from('crews').select('status'),
        supabase.from('invoices').select('amount, status'),
      ]);

      const metrics = metricsRes.data || [];
      const jobs = jobsRes.data || [];
      const crews = crewsRes.data || [];
      const invoices = invoicesRes.data || [];

      const getMetric = (key: string) => metrics.find((m: { metric_key: string }) => m.metric_key === key);

      const revenueMetric = getMetric('revenue_ytd');
      const ratingMetric = getMetric('customer_satisfaction_score');

      const ytdRevenue = invoices.filter((i: { status: string }) => i.status === 'paid').reduce((s: number, i: { amount?: number | string | null }) => s + Number(i.amount || 0), 0);
      const revenueDisplay = ytdRevenue > 0 ? formatCurrency(ytdRevenue) : (revenueMetric ? formatCurrency(Number(revenueMetric.metric_value)) : '—');

      const marginMetric = metrics.find((m: { metric_key: string }) => m.metric_key === 'average_profit_margin');
      const marginDisplay = marginMetric ? `${Number(marginMetric.metric_value).toFixed(1)}%` : '—';

      const ratingDisplay = ratingMetric ? `${Number(ratingMetric.metric_value).toFixed(1)}/5` : '—';

      const cacFromMetrics = metrics.find((m: { metric_key: string }) => m.metric_key === 'customer_acquisition_cost');
      const cacDisplay = cacFromMetrics ? `$${Number(cacFromMetrics.metric_value).toLocaleString()}` : '—';

      setKpiCards([
        { id: 'revenue', label: 'YTD Gross Revenue',         value: revenueDisplay, sub: revenueMetric ? `+${revenueMetric.trend_percentage}% MoM` : '', trend: 'up',        color: '#10B981', icon: 'CurrencyDollarIcon' },
        { id: 'margin',  label: 'Company Gross Margin',      value: marginDisplay,  sub: '',                                                             trend: 'up',        color: '#07B0E9', icon: 'ChartPieIcon' },
        { id: 'cac',     label: 'Customer Acquisition Cost', value: cacDisplay,     sub: '',                                                             trend: 'down-good', color: '#033494', icon: 'UserPlusIcon' },
        { id: 'rating',  label: 'Avg. Customer Rating',      value: ratingDisplay,  sub: ratingMetric ? ratingMetric.period_label : '',                  trend: 'up',        color: '#F59E0B', icon: 'StarIcon' },
      ]);

      // ── Dept cards with live aggregates ───────────────────────────────────
      const activeJobs = jobs.filter((j: { status: string }) => j.status === 'active').length;
      const scheduledJobs = jobs.filter((j: { status: string }) => j.status === 'scheduled').length;
      const pipelineValue = jobs
        .filter((j: { status: string }) => ['planning', 'scheduled', 'active'].includes(j.status))
        .reduce((s: number, j: { contract_value?: number | string | null }) => s + Number(j.contract_value || 0), 0);
      const activeCrews = crews.filter((c: { status: string }) => c.status === 'active').length;
      const bookedRevenue = invoices
        .filter((i: { status: string }) => ['paid', 'sent'].includes(i.status))
        .reduce((s: number, i: { amount?: number | string | null }) => s + Number(i.amount || 0), 0);

      const [qcMetrics, logisticsMetrics, financeMetrics, installerMetrics] = await Promise.all([
        supabase.from('dashboard_metrics').select('metric_key, metric_value').eq('category', 'qc'),
        supabase.from('dashboard_metrics').select('metric_key, metric_value').eq('category', 'logistics'),
        supabase.from('dashboard_metrics').select('metric_key, metric_value').eq('category', 'financial'),
        supabase.from('dashboard_metrics').select('metric_key, metric_value').eq('category', 'operations'),
      ]);

      const qcData = qcMetrics.data || [];
      const logData = logisticsMetrics.data || [];
      const finData = financeMetrics.data || [];
      const instData = installerMetrics.data || [];

      const getVal = (arr: { metric_key: string; metric_value: unknown }[], key: string, fallback: string) => {
        const m = arr.find((x) => x.metric_key === key);
        return m ? String(m.metric_value) : fallback;
      };

      setDeptCards([
        { id: 'sales',      label: 'Sales & Estimates',  icon: 'ChartBarIcon',           status: 'green',                            route: '/project-manager-dashboard',                metrics: [{ label: 'Active Jobs', value: String(activeJobs + scheduledJobs) }, { label: 'Pipeline Value', value: formatCurrency(pipelineValue) }, { label: 'Booked Revenue', value: formatCurrency(bookedRevenue) }] },
        { id: 'operations', label: 'Operations & PMs',   icon: 'CogIcon',                status: activeJobs > 0 ? 'green' : 'yellow', route: '/operations-command-center-dashboard',      metrics: [{ label: 'Active Roofs Today', value: String(activeJobs) }, { label: 'Avg Install Time', value: getVal(instData, 'avg_install_days', '—') }, { label: 'Change Order Freq.', value: getVal(instData, 'change_order_rate', '—') }] },
        { id: 'logistics',  label: 'Logistics & Supply', icon: 'TruckIcon',              status: 'red',                              route: '/logistics-supply-chain-dashboard',         metrics: [{ label: 'On-Time Delivery', value: getVal(logData, 'on_time_delivery_rate', '—') }, { label: 'Active Weather Delays', value: getVal(logData, 'active_weather_delays', '—') }, { label: 'Supplier Health Score', value: getVal(logData, 'supplier_health_score', '—') }] },
        { id: 'finance',    label: 'Finance',            icon: 'BanknotesIcon',          status: 'yellow',                           route: '/financial-accounting-dashboard',           metrics: [{ label: 'Cash in Escrow', value: getVal(finData, 'escrow_balance', '—') }, { label: 'Collections Rate', value: getVal(finData, 'collections_rate', '—') }, { label: 'Monthly Runway', value: getVal(finData, 'monthly_runway', '—') }] },
        { id: 'quality',    label: 'Quality & Warranty', icon: 'ShieldCheckIcon',        status: 'green',                            route: '/warranty-claims-dashboard',                metrics: [{ label: 'First-Time Pass Rate', value: getVal(qcData, 'first_time_pass_rate', '—') }, { label: 'Active Warranty Claims', value: getVal(qcData, 'active_warranty_claims', '—') }, { label: 'Avg Claim Resolution', value: getVal(qcData, 'avg_claim_resolution_days', '—') }] },
        { id: 'installers', label: 'Installer Network',  icon: 'WrenchScrewdriverIcon', status: activeCrews > 0 ? 'green' : 'yellow', route: '/installer-network-crew-management-dashboard', metrics: [{ label: 'Total Active Crews', value: String(activeCrews) }, { label: 'Network Capacity', value: getVal(instData, 'network_capacity', '—') }, { label: 'Compliance Drop-offs', value: getVal(instData, 'compliance_dropoffs', '—') }] },
      ]);
    } catch {
      // keep fallback data
    } finally {
      setLoadingKpis(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  // ── AI Executive Briefing (real, Gemini, session-cached) ──────────────────
  const generateAIBriefing = useCallback(async () => {
    if (briefingFetched.current) return;
    briefingFetched.current = true;

    try {
      const cached = sessionStorage.getItem(BRIEFING_CACHE_KEY);
      if (cached) {
        const { text, ts } = JSON.parse(cached);
        if (Date.now() - ts < 30 * 60 * 1000) {
          setAiBriefing(text);
          return;
        }
      }
    } catch { /* ignore */ }

    setBriefingLoading(true);

    try {
      const [escalationResult, crewResult, escrowResult, logisticsResult, arResult] = await Promise.all([
        supabase.from('escalations').select('title, priority, status, source_department').neq('status', 'resolved').limit(10),
        supabase.from('installer_crews').select('crew_name, status, capacity_percentage').limit(20),
        supabase.from('escrow_queue').select('amount, status').limit(20),
        supabase.from('logistics_jobs').select('status, delivery_date').limit(20),
        supabase.from('financial_ar').select('amount, status').limit(20),
      ]);

      const escalationData = escalationResult.data || [];
      const crewData = crewResult.data || [];
      const escrowData = escrowResult.data || [];
      const logisticsData = logisticsResult.data || [];
      const arData = arResult.data || [];

      const openEscalations = escalationData.filter((e: { status: string }) => e.status !== 'resolved');
      const highPriorityEsc = openEscalations.filter((e: { priority: string }) => e.priority === 'high' || e.priority === 'critical');
      const pendingEscrow = escrowData.filter((e: { status: string }) => e.status === 'pending');
      const escrowTotal = pendingEscrow.reduce((sum: number, e: { amount?: number | null }) => sum + (e.amount || 0), 0);
      const activeCrews = crewData.filter((c: { status: string }) => c.status === 'active');
      const highCapCrews = crewData.filter((c: { capacity_percentage?: number | null }) => (c.capacity_percentage || 0) >= 90);
      const overdueAR = arData.filter((a: { status: string }) => a.status === 'overdue');
      const overdueARTotal = overdueAR.reduce((sum: number, a: { amount?: number | null }) => sum + (a.amount || 0), 0);
      const delayedLogistics = logisticsData.filter((l: { status: string }) => l.status === 'delayed');

      const dataContext = `
Live NextRoof 360 Data Summary (as of ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}):
- Open escalations: ${openEscalations.length} total, ${highPriorityEsc.length} high/critical priority
- High-priority escalation departments: ${[...new Set(highPriorityEsc.map((e: { source_department: string }) => e.source_department))].join(', ') || 'None'}
- Pending escrow releases: ${pendingEscrow.length} items totaling $${escrowTotal.toLocaleString()}
- Active installer crews: ${activeCrews.length}, with ${highCapCrews.length} crews at 90%+ capacity
- Overdue AR: ${overdueAR.length} accounts totaling $${overdueARTotal.toLocaleString()}
- Delayed logistics jobs: ${delayedLogistics.length}
- CEO name: ${currentUser.first_name}
      `.trim();

      sendMessage([
        { role: 'system', content: 'You are SmartPick™, an AI executive assistant for NextRoof 360, a roofing company. Generate a concise, personalized morning executive briefing paragraph (2-4 sentences) for the CEO. Use the live data provided. Be specific with numbers. Highlight the most critical issues. Write in a professional, direct tone. Do not use bullet points or headers — write as a single flowing paragraph.' },
        { role: 'user', content: dataContext },
      ], { max_completion_tokens: 300 });
    } catch {
      setBriefingLoading(false);
      briefingFetched.current = false;
    }
  }, [supabase, currentUser.first_name, sendMessage]);

  useEffect(() => {
    generateAIBriefing();
  }, [generateAIBriefing]);

  useEffect(() => {
    if (chatResponse && !chatLoading) {
      setAiBriefing(chatResponse);
      setBriefingLoading(false);
      try {
        sessionStorage.setItem(BRIEFING_CACHE_KEY, JSON.stringify({ text: chatResponse, ts: Date.now() }));
      } catch { /* ignore */ }
    }
  }, [chatResponse, chatLoading]);

  const handleGenerateReport = () => {
    setReportGenerating(true);
    setTimeout(() => setReportGenerating(false), 2500);
  };

  const handleRefreshBriefing = () => {
    briefingFetched.current = false;
    setAiBriefing(null);
    try { sessionStorage.removeItem(BRIEFING_CACHE_KEY); } catch { /* ignore */ }
    generateAIBriefing();
  };

  // ── Derived: system status from worst dept ──────────────────────────────
  const systemStatus: 'green' | 'yellow' | 'red' =
    deptCards.some((d) => d.status === 'red')    ? 'red'
    : deptCards.some((d) => d.status === 'yellow') ? 'yellow'
    : 'green';

  const dateRangeOptions = [
    { key: 'today',    label: 'Today' },
    { key: 'week',     label: 'This Week' },
    { key: 'month',    label: 'This Month' },
    { key: 'quarter',  label: 'Q2 2026' },
  ] as const;

  const isBriefingLoading = briefingLoading || chatLoading;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA', fontFamily: "'Inter', 'Poppins', sans-serif" }}>
      {/* ── Sticky Header chrome ──────────────────────────────────────────── */}
      <div
        className="sticky top-[60px] z-30 border-b"
        style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      >
        <div className="flex items-center justify-between px-6 py-3.5">
          <div>
            <h1 className="font-bold text-xl" style={{ color: '#000150' }}>
              Executive Command Center
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs" style={{ color: '#6B7280' }}>
                Co-CEO strategic + financial overview
              </p>
              <span className="text-gray-300 text-xs">·</span>
              <SystemStatusPill status={systemStatus} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Date Range Toggle (cosmetic — no fetch reads this yet) */}
            <div className="hidden md:flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6' }}>
              {dateRangeOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setDateRange(opt.key)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-all"
                  style={{
                    backgroundColor: dateRange === opt.key ? '#033494' : 'transparent',
                    color: dateRange === opt.key ? '#FFFFFF' : '#6B7280',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Notification Bell (scaffolded — no panel renders yet) */}
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:bg-gray-100"
              style={{ border: '1px solid #E5E7EB' }}
            >
              <Icon name="BellIcon" size={18} className="text-gray-500" />
              <span
                className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
                style={{ backgroundColor: '#EF4444' }}
              />
            </button>

            {/* CEO Avatar */}
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}
            >
              {currentUser.first_name.charAt(0)}{currentUser.last_name.charAt(0)}
            </div>

            {/* Generate Board Report (scaffolded — 2.5s fake spinner) */}
            <button
              onClick={handleGenerateReport}
              disabled={reportGenerating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-70"
              style={{ backgroundColor: '#033494', boxShadow: '0 2px 6px rgba(3,52,148,0.3)' }}
            >
              {reportGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Icon name="DocumentChartBarIcon" size={16} />
                  Generate Board Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Content rows ──────────────────────────────────────────────────── */}
      <div className="px-6 py-6 space-y-6">
        <Row1ExecHero
          userName={currentUser.first_name}
          aiBriefing={aiBriefing}
          briefingLoading={isBriefingLoading}
          onRefreshBriefing={handleRefreshBriefing}
        />
        <Row2KPIStrip kpiCards={kpiCards} loading={loadingKpis} />
        <Row3GrowthTrajectory />
        <Row4DeptHealthMatrix deptCards={deptCards} />
        <Row5FinancialPulse />
        <Row6RiskAndAction />
        <Row7OutsideTheBuilding />
        <Row8ExternalAndRisk />
        <Row9TeamAndCulture />
      </div>
    </div>
  );
};

export default ExecutiveCommandCenterInteractive;
